import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

const ReturningCaptainLogin = ({
  onLoginSuccess,
  openTrigger,
  prefillEmail,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const { signIn, resetPasswordForEmail } = useAuth();

  const resolveEmail = (raw) =>
    raw.includes("@") ? raw : `${raw.trim()}@muqalabhoops.org`;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signIn(resolveEmail(email), password);
      onLoginSuccess?.();
      setOpen(false);
    } catch (err) {
      setError(err.message || "Login failed. Check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await resetPasswordForEmail(resolveEmail(email));
    } catch (err) {
      console.error("Reset request failed:", err);
    } finally {
      setLoading(false);
      setResetSent(true);
    }
  };

  useEffect(() => {
    if (!openTrigger) return;
    setOpen(true);
    if (prefillEmail) setEmail(prefillEmail);
  }, [openTrigger, prefillEmail]);

  if (!open) {
    return (
      <div className="mb-8 max-w-xl mx-auto transform hover:scale-[1.01] transition-all duration-200">
        <button
          onClick={() => setOpen(true)}
          className="w-full relative overflow-hidden bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 text-white p-4 sm:p-5 rounded-2xl shadow-xl shadow-orange-500/20 hover:shadow-orange-500/30 flex items-center justify-between gap-4 text-left border border-orange-400/30 group"
        >
          {/* Background Animated Court Lines Effect */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.1),transparent_70%)] pointer-events-none" />

          <div className="flex items-center gap-4 relative z-10">
            {/* High-visibility icon container with pulsing radar notification */}
            <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 text-white shrink-0 group-hover:scale-110 transition-transform">
              {/* Pulsing Light */}
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-300"></span>
              </span>
              {/* Basketball Icon SVG */}
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 2.07c3.06.42 5.51 2.87 5.93 5.93H13V4.07zM11 4.07v5.93H5.07c.42-3.06 2.87-5.51 5.93-5.93zM4.07 11H11v7.93C7.94 18.51 5.49 16.06 4.07 13zm7 7.93V13h6.93c-.42 3.06-2.87 5.51-5.93 5.93z" />
              </svg>
            </div>

            <div>
              <div className="text-base sm:text-lg tracking-tight text-gray-60/90 flex items-center gap-1.5">
                Returning Captain?
                <span className="text-amber-200 underline underline-offset-2">
                  Click Here
                </span>
              </div>
              <p className="text-xs sm:text-sm text-orange-50/90 font-medium mt-0.5">
                Log in now to quickly import your team roster and historical
                stats.
              </p>
            </div>
          </div>

          {/* Action Arrow */}
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white shrink-0 group-hover:translate-x-1 transition-transform relative z-10">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="mb-8 relative overflow-hidden bg-white/60 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/80 dark:border-gray-800/80 rounded-2xl p-6 shadow-xl shadow-gray-200/30 dark:shadow-none max-w-md mx-auto transition-all duration-300">
      {/* Decorative courtside ambient glow */}
      <div className="absolute -top-10 -right-10 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />

      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="font-extrabold text-xl bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent tracking-tight">
            {mode === "login" ? "Captain Hub" : "Reset Password"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {mode === "login"
              ? "Welcome back to the court"
              : "Get back in the game"}
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-800/60 px-2.5 py-1 rounded-full transition-colors"
        >
          Hide
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 px-3 py-2.5 rounded-xl mb-4 text-xs font-medium flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
          {error}
        </div>
      )}

      {mode === "reset" && resetSent ? (
        <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-400 p-4 rounded-xl text-sm font-medium text-center">
          🏀 Link sent! Check your inbox to reset your password.
        </div>
      ) : (
        <form
          onSubmit={mode === "login" ? handleLogin : handleReset}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 pl-1">
              Captain Email
            </label>
            <input
              type="text"
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-sm"
              required
            />
          </div>

          {mode === "login" && (
            <div className="space-y-1.5">
              <div className="flex justify-between items-center pl-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setMode("reset");
                    setError(null);
                  }}
                  className="text-xs font-semibold text-orange-600 dark:text-orange-400 hover:underline"
                >
                  Forgot?
                </button>
              </div>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-sm"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 active:scale-[0.99] transition-all disabled:opacity-50 disabled:pointer-events-none"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Syncing Rosters...
              </span>
            ) : mode === "login" ? (
              "Sign In As Captain"
            ) : (
              "Send Reset Link"
            )}
          </button>

          {mode === "reset" && (
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError(null);
              }}
              className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white block mx-auto pt-1 transition-colors"
            >
              Back to login
            </button>
          )}
        </form>
      )}
    </div>
  );
};

export default ReturningCaptainLogin;
